import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import { updateCart } from "@insite/client-framework/Services/CartService";
import {
    deleteSession,
    stopWatchingForOtherTabSessionChange,
    watchForOtherTabSessionChange,
} from "@insite/client-framework/Services/SessionService";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { AccountModel, BaseAddressModel } from "@insite/client-framework/Types/ApiModels";
import { addAccount as addAccountApi, AddAccountApiParameter } from "@tCooneyBrothers/Services/AccountService";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep";

export interface AddAccountParameter {
    IsCustomerNumberProvided: boolean;
    accountNumber: string;
    firstName: string;
    lastName: string;
    phone: string;
    userName: string;
    email: string;
    password: string;
    isSubscribed: boolean;

    companyName: string;
    address1: string;
    address2: string;
    city: string;
    postalCode: string;
    IsShipToSameAsBilling: boolean;
    // BillTo:BaseAddressModel;
    // ShipTo: BaseAddressModel;
    // billToSelectedCountry: any;
    // billToSelectedState: any;
    // shipToSelectedCountry: any;
    // shipToSelectedState: any;
    // firstNameShipTo:string;
    // lastNameShipTo:string;
    address1ShipTo: string;
    address2ShipTo: string;
    companyNameShipTo: string;
    cityShipTo: string;
    zipShipTo: string;
    IsCreditAccount: boolean;

    date?: Date;
    businessStartDate: string;
    organizationStructure: string;
    stateDateIncorporated: string;
    companyOperationalTime: string;
    principalActivity: string;
    IsCompanyNameChanged: string;
    formerName: string;
    IsPastBought: string;
    pastBuyerName: string;
    creditLimit: string;
    IsSalesTaxExempt: string;
    apContact: string;
    apContactEmail: string;
    bankName: string;
    bankAccount: string;
    bankAddress: string;
    bankPhone: string;
    bankFax: string;
    businessRefCompName1: string;
    businessRefName1: string;
    businessRefTitle1: string;
    businessRefAddress1: string;
    businessRefPhone1: string;
    businessRefFax1: string;
    businessRefCompName2: string;
    businessRefName2: string;
    businessRefTitle2: string;
    businessRefAddress2: string;
    businessRefPhone2: string;
    businessRefFax2: string;
    IsBankrupt: string;
    IsSuitFiled: string;
    IsUpsAccount: string;
    upsAccount: string;
    receivingHours: string;
    IsTerms: string;
    invEmailText: string;
    invFaxNumberText: string;
    suitFiledText: string;
    IsBankruptText: string;
    selectedDept: string | undefined;
    selectedCountry: string | undefined;
    selectedbtState: any;
    selectedStCountry: string | undefined;
    selectedStState: any;
    phoneShipTo: string;
    onSuccess?: () => void;
    onError?: (error: string, statusCode?: number) => void;
}

type HandlerType = ApiHandlerDiscreteParameter<
    AddAccountParameter,
    AddAccountApiParameter,
    ServiceResult<AccountModel>
>;

export const UnassignCartFromGuest: HandlerType = async props => {
    const { value: cart } = getCurrentCartState(props.getState());
    const currentUserIsGuest = getCurrentUserIsGuest(props.getState());
    if (!currentUserIsGuest) {
        return;
    }
    if (!cart) {
        throw new Error("The cart is not loaded. Try reloading the page.");
    }

    if (cart.lineCount > 0) {
        const cartClone = cloneDeep(cart) as Draft<typeof cart>;
        cartClone.unassignCart = true;
        await updateCart({ cart: cartClone });
    }
};

export const SignOutGuest: HandlerType = async props => {
    const currentUserIsGuest = getCurrentUserIsGuest(props.getState());
    if (!currentUserIsGuest) {
        return;
    }

    await deleteSession();
    props.dispatch({
        type: "Context/DeleteSession",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        account: ({
            ...props.parameter,
            properties: {
                IsCustomerNumberProvided: props.parameter.IsCustomerNumberProvided,
                Department: props.parameter.selectedDept, // "Accounts",
                CustomerNumber: props.parameter.accountNumber,
                UserPhone: props.parameter.phone,
                Company: props.parameter.companyName,
                Address1: props.parameter.address1,
                Address2: props.parameter.address2,
                City: props.parameter.city,
                Zip: props.parameter.postalCode,
                IsShipToSameAsBilling: props.parameter.IsShipToSameAsBilling,
                StateBillTo: props.parameter.selectedbtState,
                CountryBillTo: props.parameter.selectedCountry,
                Email: props.parameter.email,
                FirstName: props.parameter.firstName,

                IsCreditAccount: props.parameter.IsCreditAccount,
                FirstNameShipTo: props.parameter.firstName,
                LastNameShipTo: props.parameter.lastName,
                CompanyShipTo:
                    props.parameter.IsShipToSameAsBilling || props.parameter.companyNameShipTo.trim() === ""
                        ? props.parameter.companyName
                        : props.parameter.companyNameShipTo,
                Address1ShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.address1
                    : props.parameter.address1ShipTo,
                Address2ShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.address2
                    : props.parameter.address2ShipTo,
                CityShipTo: props.parameter.IsShipToSameAsBilling ? props.parameter.city : props.parameter.cityShipTo,
                ZipShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.postalCode
                    : props.parameter.zipShipTo,
                StateShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.selectedbtState
                    : props.parameter.selectedStState,
                CountryShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.selectedCountry
                    : props.parameter.selectedStCountry,
                PhoneShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.phone
                    : props.parameter.phoneShipTo,
                Date: props.parameter.date ?? new Date(),
                OrganizationStructure: props.parameter.organizationStructure,
                BusinessStartDate: props.parameter.businessStartDate,
                StateDateIncorporated: props.parameter.stateDateIncorporated,
                CompanyOperationalTime: props.parameter.companyOperationalTime,
                PrincipalActivity: props.parameter.principalActivity,
                IsCompanyNameChanged: props.parameter.IsCompanyNameChanged,
                FormerName: props.parameter.formerName,
                IsPastBought: props.parameter.IsPastBought,
                PastBuyerName: props.parameter.pastBuyerName,
                CreditLimit: props.parameter.creditLimit,
                IsSalesTaxExempt: props.parameter.IsSalesTaxExempt,
                ApContact: props.parameter.apContact,
                ApContactEmail: props.parameter.apContactEmail,
                IsinvoiceSentType: "",
                InvoiceSentEmail: props.parameter.invEmailText,
                InvoiceSentFaxNum: props.parameter.invFaxNumberText,
                InvoiceSentUsPostMail: "",

                BankName: props.parameter.bankName,
                BankAccount: props.parameter.bankAccount,
                BankAddress: props.parameter.bankAddress,
                BankPhone: props.parameter.bankPhone,
                BankFax: props.parameter.bankFax,
                BusinessRefCompName1: props.parameter.businessRefCompName1,
                BusinessRefName1: props.parameter.businessRefName1,
                BusinessRefTitle1: props.parameter.businessRefTitle1,
                BusinessRefAddress1: props.parameter.businessRefAddress1,
                BusinessRefPhone1: props.parameter.businessRefPhone1,
                BusinessRefFax1: props.parameter.businessRefFax1,
                BusinessRefCompName2: props.parameter.businessRefCompName2,
                BusinessRefName2: props.parameter.businessRefName2,
                BusinessRefTitle2: props.parameter.businessRefTitle2,
                BusinessRefAddress2: props.parameter.businessRefAddress2,
                BusinessRefPhone2: props.parameter.businessRefPhone2,
                BusinessRefFax2: props.parameter.businessRefFax2,
                IsBankrupt: props.parameter.IsBankrupt,
                YesBankruptExplain: props.parameter.IsBankruptText,
                IsSuitFiled: props.parameter.IsSuitFiled,
                YesSuitExplain: props.parameter.suitFiledText,
                IsUpsAccount: props.parameter.IsUpsAccount,
                UpsAccount: props.parameter.upsAccount,
                ReceivingHours: props.parameter.receivingHours,
                IsTerms: props.parameter.IsTerms,
            } as object,
        } as unknown) as AccountModel,
    };
};

export const AddAccount: HandlerType = async props => {
    stopWatchingForOtherTabSessionChange();
    props.apiResult = await addAccountApi(props.apiParameter);
    watchForOtherTabSessionChange();
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage!, props.apiResult.statusCode);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.();
    }
};

export const chain = [
    UnassignCartFromGuest,
    SignOutGuest,
    PopulateApiParameter,
    AddAccount,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
];

const addAccount = createHandlerChainRunner(chain, "AddAccount");
export default addAccount;
